import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Divider, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Checkbox, CircularProgress, Alert, Snackbar,
  Avatar, List, ListItem, ListItemText, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AccessReportSection from './AccessReportSection';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SavingsIcon from '@mui/icons-material/Savings';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonIcon from '@mui/icons-material/Person';

// ── Shared primitives ────────────────────────────────────────────────────────

// ── Design tokens (matches AccountDetailPage) ────────────────────────────────
const T = {
  meta:   { fontSize: '0.8rem' },
  body:   { fontSize: '1rem' },
  title:  { fontSize: '1.25rem', fontWeight: 700, color: '#444444' },
  button: { fontSize: '1rem', textTransform: 'none' },
};

const COLORS = {
  notif: '#6a1b9a',
};

function SectionHeader({ icon, title, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {React.cloneElement(icon, { sx: { fontSize: 28, color: '#444444' } })}
        <Typography sx={{ ...T.title }}>{title}</Typography>
      </Box>
      {action}
    </Box>
  );
}

// Derive initials from an email address (use local part before @)
function getInitials(email) {
  if (!email || typeof email !== 'string') return '?';
  const local = email.split('@')[0];
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

// ── Email list display — avatar + email rows (Key Contacts style) ────────────

function EmailList({ emails, color = COLORS.notif }) {
  const safeEmails = (emails || []).filter(e => e != null).map(e => String(e));
  if (safeEmails.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.5, color: 'text.disabled' }}>
        <PersonIcon sx={{ fontSize: 24, mb: 0.5 }} />
        <Typography sx={{ ...T.body }}>No recipients configured</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {safeEmails.map((email, idx) => (
        <Box key={`${email}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Avatar sx={{
            width: 34, height: 34, fontSize: '0.95rem', fontWeight: 700,
            letterSpacing: '1px', textIndent: '1px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: color, flexShrink: 0,
          }}>
            {getInitials(email)}
          </Avatar>
          <Typography sx={{ ...T.body, wordBreak: 'break-all' }}>{email}</Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Helper: parse textarea → email list ─────────────────────────────────────
function parseEmails(text) {
  return text.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
}

// ── Reusable recipients edit dialog (Exchange Mailbox DL style) ──────────────

function RecipientsDialog({ open, title, emails, onClose, onSave, saving, saveError, extra }) {
  const [selected, setSelected] = useState([]);
  const [addInput, setAddInput] = useState('');
  const [addResults, setAddResults] = useState(null);

  useEffect(() => {
    if (open) { setSelected([]); setAddInput(''); setAddResults(null); }
  }, [open]);

  const toggleSelect = (email) =>
    setSelected(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);

  const handleRemove = () => {
    const remaining = (emails || []).filter(e => !selected.includes(e));
    onSave({ emails: remaining });
    setSelected([]);
  };

  const handleAdd = () => {
    const newEmails = parseEmails(addInput).filter(e => !(emails || []).includes(e));
    if (!newEmails.length) return;
    const merged = [...(emails || []), ...newEmails];
    onSave({ emails: merged, addedEmails: newEmails });
    setAddInput('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '0.95rem', pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        {saveError && <Alert severity="error" sx={{ fontSize: '0.8rem', mb: 1.5 }}>{saveError}</Alert>}

        {/* Current recipients with checkboxes */}
        {emails && emails.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
              Current Recipients
            </Typography>
            <List dense disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 200, overflow: 'auto' }}>
              {emails.filter(e => e != null).map((email, idx) => {
                const emailStr = String(email);
                const isSelected = selected.includes(emailStr);
                return (
                  <ListItem key={`${emailStr}-${idx}`} disablePadding
                    sx={{ px: 1, py: 0.25, borderBottom: idx < emails.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                    <Checkbox size="small" checked={isSelected} onChange={() => toggleSelect(emailStr)} sx={{ p: 0.25, mr: 0.5 }} />
                    <ListItemText disableTypography
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Avatar sx={{ width: 22, height: 22, fontSize: '0.6rem', bgcolor: 'primary.main' }}>
                            {getInitials(emailStr)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', flex: 1, wordBreak: 'break-all' }}>
                            {emailStr}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
            <Box sx={{ mt: 1 }}>
              <Button variant="outlined" color="error" size="small"
                startIcon={saving ? <CircularProgress size={13} /> : <DeleteOutlineIcon />}
                onClick={handleRemove}
                disabled={selected.length === 0 || saving}
                sx={{ fontSize: '0.75rem' }}>
                {saving ? 'Removing...' : `Remove Selected (${selected.length})`}
              </Button>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Extra fields (frequency, subscription, CC) */}
        {extra}

        {/* Add recipients textarea */}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
            Add Recipients
          </Typography>
          <TextField multiline rows={3} fullWidth size="small"
            placeholder={"email1@example.com\nemail2@example.com"}
            value={addInput}
            onChange={e => setAddInput(e.target.value)}
            disabled={saving}
            helperText="One email per line or comma-separated"
            sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem' }, '& .MuiFormHelperText-root': { fontSize: '0.7rem' } }}
          />
          {addResults && addResults.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {addResults.map((r, i) => (
                <Tooltip key={i} title={r.success ? 'Added' : 'Already exists'}>
                  <Chip label={r.email} size="small"
                    color={r.success ? 'success' : 'warning'} variant="outlined"
                    sx={{ fontSize: '0.65rem', height: 20 }} />
                </Tooltip>
              ))}
            </Box>
          )}
          <Box sx={{ mt: 1 }}>
            <Button variant="contained" size="small"
              startIcon={saving ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : null}
              onClick={handleAdd}
              disabled={!addInput.trim() || saving}
              sx={{ fontSize: '0.75rem' }}>
              {saving ? 'Adding...' : 'Add Recipients'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function AccountAttributesInfo({ account, authenticatedFetch, API_ENDPOINT, canEdit, onDataLoaded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [accessReportOpen, setAccessReportOpen] = useState(false);
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [costOptOpen, setCostOptOpen] = useState(false);

  // Per-dialog extra field state (frequency, subscription, CC)
  const [frequency, setFrequency] = useState('monthly');
  const [subscription, setSubscription] = useState(false);
  const [ccList, setCcList] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadAttributes = async () => {
    if (!account?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedFetch(`${API_ENDPOINT}/accounts/${account.id}/attributes`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'Failed to load account attributes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAttributes(); }, [account?.id, authenticatedFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lift data to parent for export whenever it changes
  useEffect(() => { if (data && onDataLoaded) onDataLoaded(data); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync dialog extra fields when data loads
  useEffect(() => {
    if (data) {
      setFrequency(data.access_report_frequency || 'monthly');
      setSubscription(!!data.access_report_subscription);
    }
  }, [data]);

  // closeDialog: whether to close the dialog after saving (false for settings-only saves)
  const handleSave = async (fields, { closeDialog = true } = {}) => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await authenticatedFetch(`${API_ENDPOINT}/accounts/${account.id}/attributes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const json = await res.json();
      if (!res.ok) {
        setSaveError(json.error || 'Save failed. Please try again.');
        return;
      }
      setData(json);
      if (closeDialog) {
        setAccessReportOpen(false);
        setComplianceOpen(false);
        setCostOptOpen(false);
      }
      setSnackbar({ open: true, message: 'Saved successfully', severity: 'success' });
    } catch (e) {
      setSaveError(e.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Access Report recipients save — only saves the email list (settings saved separately)
  const handleAccessReportSave = ({ emails }) => {
    handleSave({ access_report_recipients: emails });
  };

  // Access Report settings save — only saves frequency + subscription, keeps dialog open
  const handleAccessReportSettingsSave = () => {
    handleSave(
      { access_report_frequency: frequency, access_report_subscription: subscription },
      { closeDialog: false }
    );
  };

  // Compliance Alert save — merges TO list with CC
  const handleComplianceSave = ({ emails }) => {
    handleSave({
      config_alerts_email: emails,
      config_alerts_email_cc: parseEmails(ccList),
    });
  };

  const editBtn = (onClick) => canEdit ? (
    <Tooltip title="Edit">
      <IconButton size="small" onClick={onClick} sx={{ p: 0.25 }}>
        <EditIcon sx={{ fontSize: 24, color: 'action.active' }} />
      </IconButton>
    </Tooltip>
  ) : null;

  const cardContent = (content) => {
    if (loading) return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>Loading...</Typography>
      </Box>
    );
    if (error) return <Alert severity="error" sx={{ fontSize: '0.78rem' }}>{error}</Alert>;
    return content;
  };

  // Access Report extra fields rendered inside the dialog
  const accessReportExtra = (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 1 }}>Settings</Typography>
      <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
        <InputLabel>Frequency</InputLabel>
        <Select value={frequency} label="Frequency" onChange={e => setFrequency(e.target.value)}>
          {['daily', 'weekly', 'monthly', 'quarterly'].map(f => (
            <MenuItem key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</MenuItem>
          ))}
        </Select>      </FormControl>
      <FormControlLabel
        control={<Checkbox checked={subscription} onChange={e => setSubscription(e.target.checked)} size="small" />}
        label={<Typography sx={{ fontSize: '0.85rem' }}>Subscription enabled</Typography>}
      />
      <Box sx={{ mt: 1 }}>
        <Button variant="outlined" size="small"
          startIcon={saving ? <CircularProgress size={13} /> : null}
          onClick={handleAccessReportSettingsSave}
          disabled={saving}
          sx={{ fontSize: '0.75rem' }}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
      <Divider sx={{ my: 1.5 }} />
    </Box>
  );

  // Compliance CC extra field rendered inside the dialog
  const complianceCcExtra = (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>CC Recipients</Typography>
      {data?.config_alerts_email_cc?.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1, pl: 0.25 }}>
          {data.config_alerts_email_cc.map(email => (
            <Box key={email} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Avatar sx={{ width: 22, height: 22, fontSize: '0.6rem', bgcolor: 'secondary.main' }}>
                {getInitials(email)}
              </Avatar>
              <Typography variant="body2" sx={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>{email}</Typography>
            </Box>
          ))}
        </Box>
      )}
      <TextField
        label="CC (add/replace)"
        multiline rows={2} fullWidth size="small"
        placeholder={"cc1@example.com\ncc2@example.com"}
        value={ccList}
        onChange={e => setCcList(e.target.value)}
        helperText="Replaces current CC list — one per line or comma-separated"
        sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem' }, '& .MuiFormHelperText-root': { fontSize: '0.7rem' } }}
      />
      <Divider sx={{ my: 1.5 }} />
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 2, alignItems: 'start' }}>

        {/* Access Reports */}
        <Box>
          <SectionHeader
            icon={<EmailIcon />}
            title="Access Reports"
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {data?.access_report_recipients?.length > 0 && (
                  <Chip label={data.access_report_recipients.length} size="small" sx={{ height: 18, ...T.meta }} />
                )}
                {editBtn(() => { setSaveError(null); setAccessReportOpen(true); })}
              </Box>
            }
          />
          {cardContent(
            <>
              {(data?.access_report_frequency || data?.access_report_subscription !== undefined) && (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ ...T.body, fontWeight: 600, minWidth: 140, flexShrink: 0 }}>Frequency</Typography>
                    <Typography sx={{ ...T.body, color: 'text.secondary' }}>{data?.access_report_frequency || '—'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography sx={{ ...T.body, fontWeight: 600, minWidth: 140, flexShrink: 0 }}>Subscription</Typography>
                    <Typography sx={{ ...T.body, color: 'text.secondary' }}>{data?.access_report_subscription ? 'Enabled' : 'Disabled'}</Typography>
                  </Box>
                </Box>
              )}
              <EmailList emails={data?.access_report_recipients} />
            </>
          )}
          {account?.id && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <AccessReportSection
                accountId={account?.id}
                authenticatedFetch={authenticatedFetch}
                API_ENDPOINT={API_ENDPOINT}
                canEdit={canEdit}
              />
            </>
          )}
        </Box>

        {/* Compliance & Governance */}
        <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
          <SectionHeader
            icon={<NotificationsIcon />}
            title="Compliance & Governance"
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {(data?.config_alerts_email?.length > 0 || data?.config_alerts_email_cc?.length > 0) && (
                  <Chip
                    label={(data?.config_alerts_email?.length || 0) + (data?.config_alerts_email_cc?.length || 0)}
                    size="small" sx={{ height: 18, ...T.meta }}
                  />
                )}
                {editBtn(() => {
                  setSaveError(null);
                  setCcList((data?.config_alerts_email_cc || []).join('\n'));
                  setComplianceOpen(true);
                })}
              </Box>
            }
          />
          {cardContent(<EmailList emails={data?.config_alerts_email} />)}
        </Box>

        {/* Cost Optimization */}
        <Box sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
          <SectionHeader
            icon={<SavingsIcon />}
            title="Cost Optimization"
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {data?.cost_optimization?.length > 0 && (
                  <Chip label={data.cost_optimization.length} size="small" sx={{ height: 18, ...T.meta }} />
                )}
                {editBtn(() => { setSaveError(null); setCostOptOpen(true); })}
              </Box>
            }
          />
          {cardContent(<EmailList emails={data?.cost_optimization} />)}
        </Box>

      </Box>

      {/* ── Edit Dialogs ── */}
      <RecipientsDialog
        open={accessReportOpen}
        title="Edit Access Report Recipients"
        emails={data?.access_report_recipients}
        onClose={() => setAccessReportOpen(false)}
        onSave={handleAccessReportSave}
        saving={saving}
        saveError={saveError}
        extra={accessReportExtra}
      />

      <RecipientsDialog
        open={complianceOpen}
        title="Edit Compliance Alert Recipients (TO)"
        emails={data?.config_alerts_email}
        onClose={() => setComplianceOpen(false)}
        onSave={handleComplianceSave}
        saving={saving}
        saveError={saveError}
        extra={complianceCcExtra}
      />

      <RecipientsDialog
        open={costOptOpen}
        title="Edit Cost Optimization Recipients"
        emails={data?.cost_optimization}
        onClose={() => setCostOptOpen(false)}
        onSave={({ emails }) => handleSave({ cost_optimization: emails })}
        saving={saving}
        saveError={saveError}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ fontSize: '0.82rem' }}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
}
